import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Navigation2,
  MapPin,
  ChevronUp,
  ChevronDown,
  Clock,
  Compass,
  ArrowUpRight,
  ArrowUpLeft,
} from "lucide-react";
import { Department, Route } from "../types";

interface PositioningPanelProps {
  kioskId: string;
  destination: Department;
  route?: Route;
  onBack: () => void;
  onStepChange?: (stepIndex: number) => void;
}

const PositioningPanel: React.FC<PositioningPanelProps> = ({
  kioskId,
  destination,
  route,
  onBack,
  onStepChange,
}) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isStepsCollapsed, setIsStepsCollapsed] = useState(false);

  const handleStepChange = (index: number) => {
    setCurrentStepIndex(index);
    onStepChange?.(index);
  };

  const getDirectionIcon = (instruction: string) => {
    const instrLower = instruction.toLowerCase();

    if (instrLower.includes("turn left")) {
      return <ArrowUpLeft size={24} className="text-red-500" />;
    }
    if (instrLower.includes("turn right")) {
      return <ArrowUpRight size={24} className="text-red-500" />;
    }
    if (
      instrLower.includes("forward") ||
      instrLower.includes("continue") ||
      instrLower.includes("straight")
    ) {
      return <Navigation2 size={24} className="text-cyan-500" />;
    }
    if (instrLower.includes("arrive") || instrLower.includes("destination")) {
      return <MapPin size={24} className="text-green-500" />;
    }
    return <Compass size={24} className="text-blue-600" />;
  };

  const currentStep = route?.segments[currentStepIndex];
  const totalSteps = route?.segments.length || 0;
  const progress =
    totalSteps > 0 ? ((currentStepIndex + 1) / totalSteps) * 100 : 0;

  const containerVariants = {
    initial: { x: -400, opacity: 0 },
    animate: {
      x: 0,
      opacity: 1,
      transition: { duration: 0.3, ease: "easeOut" },
    },
    exit: { x: -400, opacity: 0, transition: { duration: 0.2 } },
  };

  const stepVariants = {
    initial: { y: 20, opacity: 0 },
    animate: { y: 0, opacity: 1, transition: { duration: 0.3 } },
    exit: { y: -20, opacity: 0, transition: { duration: 0.2 } },
  };

  const stepListVariants = {
    initial: { opacity: 0 },
    animate: {
      opacity: 1,
      transition: { staggerChildren: 0.05, delayChildren: 0.1 },
    },
  };

  const stepItemVariants = {
    initial: { opacity: 0, x: -20 },
    animate: {
      opacity: 1,
      x: 0,
      transition: { type: "spring", stiffness: 100 },
    },
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="fixed left-0 top-16 bottom-0 w-full md:w-96 bg-white overflow-y-auto z-40 shadow-lg border-r border-gray-200"
    >
      {/* Scrollbar styling */}
      <style>{`
        .directions-scroll::-webkit-scrollbar {
          width: 6px;
        }
        .directions-scroll::-webkit-scrollbar-track {
          background: rgba(0,0,0,0.05);
          border-radius: 10px;
        }
        .directions-scroll::-webkit-scrollbar-thumb {
          background: rgba(0,0,0,0.2);
          border-radius: 10px;
        }
      `}</style>

      <div className="directions-scroll h-full overflow-y-auto">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="sticky top-0 bg-white border-b border-gray-200 p-4 z-50"
        >
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-gray-700 hover:bg-gray-100 px-3 py-2 rounded-lg transition-colors font-semibold"
          >
            <ArrowLeft size={20} />
            Back to Start
          </button>
        </motion.div>

        {/* Destination Card */}
        <motion.div variants={stepVariants} className="p-4 space-y-4">
          <motion.div
            whileHover={{ scale: 1.01 }}
            className="bg-blue-50 rounded-xl p-4 border border-blue-200"
          >
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-500 rounded-lg">
                <MapPin size={20} className="text-white" />
              </div>
              <div className="flex-1">
                <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide">
                  Find Destination
                </p>
                <h2 className="text-xl font-bold text-gray-900 mt-1">
                  {destination.name}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {destination.category} • {destination.serviceType}
                </p>
                {destination.description && (
                  <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                    {destination.description}
                  </p>
                )}
              </div>
            </div>
          </motion.div>

          {/* Progress Bar */}
          {route && totalSteps > 0 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="bg-gray-200 rounded-full overflow-hidden h-2"
            >
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="h-full bg-blue-500"
              />
            </motion.div>
          )}

          {/* Step Counter */}
          {route && (
            <div className="flex items-center justify-between text-gray-700 text-sm font-semibold">
              <span className="flex items-center gap-2">
                <Clock size={16} />
                Step {currentStepIndex + 1} of {totalSteps}
              </span>
              <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full">
                {Math.round(progress)}%
              </span>
            </div>
          )}
        </motion.div>

        {/* Collapsible Navigation Steps Section */}
        {route && (
          <div className="px-4 pb-4">
            <button
              onClick={() => setIsStepsCollapsed(!isStepsCollapsed)}
              className="w-full flex items-center justify-between p-3 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors mb-3"
            >
              <h3 className="text-gray-900 font-bold text-sm uppercase tracking-wide">
                Navigation Steps
              </h3>
              {isStepsCollapsed ? (
                <ChevronDown size={20} className="text-gray-600" />
              ) : (
                <ChevronUp size={20} className="text-gray-600" />
              )}
            </button>

            <AnimatePresence>
              {!isStepsCollapsed && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  {/* Current Step Highlight */}
                  <AnimatePresence mode="wait">
                    {currentStep && (
                      <motion.div
                        key={currentStepIndex}
                        variants={stepVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        className="mb-3"
                      >
                        <motion.div
                          whileHover={{ scale: 1.01 }}
                          className="bg-blue-50 rounded-xl p-4 border-2 border-blue-500"
                        >
                          <div className="flex items-start gap-3">
                            <div className="mt-1">
                              {getDirectionIcon(currentStep.instruction)}
                            </div>
                            <div className="flex-1">
                              <p className="text-xs font-semibold text-blue-600 uppercase">
                                Current Step
                              </p>
                              <h3 className="text-base font-bold text-gray-900 mt-1">
                                {currentStep.instruction}
                              </h3>
                              {currentStep.distance && (
                                <p className="text-sm text-gray-600 mt-1">
                                  Distance: ~{currentStep.distance}
                                </p>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Steps List */}
                  <motion.div
                    variants={stepListVariants}
                    initial="initial"
                    animate="animate"
                    className="space-y-2"
                  >
                    <AnimatePresence>
                      {route.segments.map((segment, index) => {
                        const isActive = index === currentStepIndex;
                        return (
                          <motion.button
                            key={index}
                            variants={stepItemVariants}
                            whileHover={{ scale: 1.01, x: 2 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => handleStepChange(index)}
                            className={`w-full text-left p-3 rounded-lg transition-all ${
                              isActive
                                ? "bg-blue-100 border-2 border-blue-500"
                                : "bg-gray-50 hover:bg-gray-100 border border-gray-200"
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              {/* Step Icon */}
                              <div
                                className={`mt-1 ${
                                  isActive ? "text-blue-600" : "text-gray-500"
                                }`}
                              >
                                {getDirectionIcon(segment.instruction)}
                              </div>

                              {/* Step Content */}
                              <div className="flex-1 min-w-0">
                                <p
                                  className={`font-semibold truncate ${
                                    isActive
                                      ? "text-gray-900"
                                      : "text-gray-700 text-sm"
                                  }`}
                                >
                                  {segment.instruction}
                                </p>
                                {segment.distance && (
                                  <p
                                    className={`text-xs mt-1 ${
                                      isActive
                                        ? "text-gray-600"
                                        : "text-gray-500"
                                    }`}
                                  >
                                    {segment.distance}
                                  </p>
                                )}
                              </div>

                              {/* Step Number Badge */}
                              <div
                                className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center font-bold text-xs ${
                                  isActive
                                    ? "bg-blue-500 text-white"
                                    : "bg-gray-300 text-gray-700"
                                }`}
                              >
                                {index + 1}
                              </div>
                            </div>
                          </motion.button>
                        );
                      })}
                    </AnimatePresence>
                  </motion.div>

                  {/* Navigation Controls */}
                  {currentStep && (
                    <motion.div
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.2 }}
                      className="mt-4 pt-4 border-t border-gray-200"
                    >
                      <div className="flex gap-2">
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() =>
                            handleStepChange(Math.max(0, currentStepIndex - 1))
                          }
                          disabled={currentStepIndex === 0}
                          className="flex-1 py-2 bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all text-sm"
                        >
                          <ChevronUp size={16} />
                          Previous
                        </motion.button>

                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() =>
                            handleStepChange(
                              Math.min(totalSteps - 1, currentStepIndex + 1),
                            )
                          }
                          disabled={currentStepIndex === totalSteps - 1}
                          className="flex-1 py-2 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-semibold flex items-center justify-center gap-2 transition-all text-sm"
                        >
                          Next
                          <ChevronDown size={16} />
                        </motion.button>
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Empty State */}
        {!route && (
          <motion.div
            variants={stepVariants}
            className="px-4 py-8 text-center text-gray-500"
          >
            <Navigation2 size={48} className="mx-auto mb-3 text-gray-400" />
            <p className="font-semibold">No route available</p>
            <p className="text-sm mt-1">
              Select a destination to calculate a route
            </p>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

export default PositioningPanel;

